import { FormattedMessage, useIntl } from 'react-intl';
import React from 'react';
import bemify from 'bemify-js';

import { Alert, Spacer, Text } from '@zapier/design-system';

import ContentList from 'app/common/components/ContentList';
import Link from 'app/common/components/Link';

import './InstallNode.scss';
import BashCodeBlock from '../components/BashCodeBlock';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';

const bem = bemify('install-node');

const InstallNode = () => {
  const intl = useIntl();

  return (
    <div className={bem()}>
      <StepTitle>
        <FormattedMessage
          defaultMessage="Install Node.js"
          id="headings-install-node-js"
        />
      </StepTitle>
      <Paragraph>
        <FormattedMessage
          defaultMessage="The Zapier CLI is a Node.js based SDK you'll use to build and deploy a Zapier App to instantly connect your API to hundreds of other applications. This quick guide will have you coding in minutes."
          id="descriptions-cli-sdk-overview"
        />
      </Paragraph>
      <Text margin="0 0 1em" tag="h2" type="SectionHeader">
        <FormattedMessage
          defaultMessage="Installation"
          id="headings-installation"
        />
      </Text>
      <Paragraph>
        <FormattedMessage
          defaultMessage="An easy option to get set up with Node.js is to visit <component0>https://nodejs.org/en/download/</component0> and download the official installer for your OS. If you're installing with a <component1>package manager</component1> it's even easier."
          id="descriptions-nodejs-install-options"
          values={{
            component0: chunks => (
              <Link href="https://nodejs.org/en/download/" target="_blank">
                {chunks}
              </Link>
            ),
            component1: chunks => (
              <Link
                href="https://nodejs.org/en/download/package-manager/"
                target="_blank"
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          defaultMessage="After installation, confirm that Node.js is ready to use:"
          id="descriptions-confirm-nodejs-installation"
        />
      </Paragraph>
      <BashCodeBlock
        lines={[
          {
            content: 'node --version',
            isCommand: true,
          },
          {
            content: 'v14.19.1',
            isComment: true,
          },
        ]}
      />
      <Alert
        status="info"
        title={intl.formatMessage({
          id: 'headings-nodejs-compatibility-note',
          defaultMessage: 'A Note on Node.js Compatibility:',
        })}
      >
        <FormattedMessage
          defaultMessage="While you can install and run the Zapier CLI using any recent version of Node.js, you will want to check the <component0>latest requirements</component0> to ensure that you are using a version that is compatible with Zapier CLI apps. Why? In production your code runs on AWS Lambda and presently this is the version our platform uses there. When the Zapier CLI builds your code we'll check to make sure all the features you are using will be supported in production. To see the differences between Node.js versions, visit <component1>node.green</component1> ."
          id="descriptions-nodejs-compatibility-explanation"
          values={{
            component0: chunks => (
              <Link
                href="https://docs.zapier.com/platform/build-cli/overview#requirements"
                target="_blank"
              >
                {chunks}
              </Link>
            ),
            component1: chunks => (
              <Link href="http://node.green/" target="_blank">
                {chunks}
              </Link>
            ),
          }}
        />
      </Alert>
      <Spacer height={22} />
      <Alert
        status="warning"
        title={intl.formatMessage({
          id: 'headings-guided-tour-requirements',
          defaultMessage:
            'Other requirements to follow along with this guided tour',
        })}
      >
        <FormattedMessage
          defaultMessage="This tutorial helps you get the CLI tool installed and configured. Then we'll clone a complete Zapier app that you will deploy and use to create Zaps. This part's optional, but we think following along will get you familiar with the important bits of the tool as fast as possible. For the guided tour you'll need to:"
          id="descriptions-guided-tour-prerequisites"
        />
        <ContentList kind="indented">
          <li>
            <FormattedMessage
              defaultMessage="<component0>Sign up</component0> for a free Zapier account, naturally."
              id="descriptions-free-zapier-account-requirement"
              values={{
                component0: chunks => (
                  <Link href="/sign-up/" target="_blank">
                    {chunks}
                  </Link>
                ),
              }}
            />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="Have <component0>git</component0> installed."
              id="descriptions-git-installation-requirement"
              values={{
                component0: chunks => (
                  <Link href="https://git-scm.com/" target="_blank">
                    {chunks}
                  </Link>
                ),
              }}
            />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="Have an account on <component0>GitHub.com</component0> ."
              id="descriptions-github-account-requirement"
              values={{
                component0: chunks => (
                  <Link href="https://github.com/" target="_blank">
                    {chunks}
                  </Link>
                ),
              }}
            />
          </li>
        </ContentList>
      </Alert>
    </div>
  );
};

export default InstallNode;
